/**
 * Registers the global Cmd+K (Mac) / Ctrl+K (Windows/Linux) keyboard
 * shortcut that toggles the command palette.
 *
 * Mount once near the top of the app tree (e.g. inside `RootLayout`).
 * Re-mounting is safe — the listener is idempotent because it cleans up
 * on unmount.
 */
import { useEffect } from 'react';

import { useCommandPaletteStore } from '@/stores/command-palette.store';

export function useCommandPaletteShortcut(): void {
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const toggle = useCommandPaletteStore((s) => s.toggle);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent): void {
      // Cmd+K on Mac, Ctrl+K elsewhere.
      const isCmdK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
      if (!isCmdK) return;
      // Don't swallow the browser's address-bar focus shortcut when modifiers
      // include Shift/Alt — only the bare combo opens the palette.
      if (e.shiftKey || e.altKey) return;
      e.preventDefault();
      toggle();
    }

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [toggle]);
}
